import React, { Component } from 'react';
import { Link } from 'react-router';
import TextField from '../component/form/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '../component/Typography';
import withStyles from '@mui/styles/withStyles';
import Button from '@mui/material/Button';
import { Mutation } from '../reactApolloCompat';
import gql from 'graphql-tag';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import AnonPage from './layout/AnonPage';
import { setToken, setTokenExpiryAfterNext30Days, setTokenExpiryAfterNextDay } from '../util/sessions';
import InputAdornment from '@mui/material/InputAdornment';
import Icon from '@mui/material/Icon';
import { joinDefined, SIGN_IN_REFERRER } from '../util/strings';
import { getAuthClient, getClient, getServiceURLHostname, getUtilitiesClient } from '../apollo';
import { isLocalClient, isLocalServer } from '../environment';
import Spinner from '../component/Spinner';
import NextIcon from '../component/icon/NextIcon';
import CircularProgress from '@mui/material/CircularProgress';

const getOfficeNames = gql`
    query {
        readOfficesUsernames {
            edges {
                node {
                    ID
                    LegacyKey
                    RegistrantSuffix
                    BusinessName
                }
            }
        }
    }
`;

class SignIn extends Component {
    state = {
        offices: null,
        email: '',
        password: '',
        lastofficeid: '',
        busy: false
    };
    _isMounted = false;
    componentWillUnmount() {
        this._isMounted = false;
    }

    onChangeText = name => e => {
        this.setState({
            [name]: e.target.value,
            error: false
        });
    };

    onToggleRemember = e => {
        this.setState({ remember: e.target.checked });
    };

    onCompleted = ({ data }, remember) => {
        const { token: Token, member: Member } = (data && (data.createTokenFromHash || data.createToken)) || {};
        const { lastofficeid } = this.state;
        if (!Token) {
            this.setState({ error: 'Incorrect email or password.', busy: false });
            return null;
        }
        const { ID, FirstName, Surname } = Member;
        setToken(
            {
                userid: ID,
                value: Token,
                name: joinDefined([FirstName, Surname], ' '),
                expiry: +remember ? setTokenExpiryAfterNext30Days() : setTokenExpiryAfterNextDay()
            },
            parseInt(remember)
        );

        if (!!ID && !!lastofficeid) {
            const that = this;
            getClient()
                .mutate({
                    mutation: updateMemberLastOfficeIDMutation,
                    variables: {
                        memberid: ID,
                        lastofficeid: lastofficeid
                    }
                })
                .then(() => that.redirection());
        } else {
            this.redirection();
        }
    };

    componentDidMount() {
        this._isMounted = true;
        if (!(isLocalClient() && !isLocalServer())) {
            const { match } = this.props;
            const { id, hash, remember } = match.params;
            getAuthClient()
                .mutate({
                    mutation: createTokenFromHash,
                    variables: { id, hash }
                })
                .then(data => {
                    if (this._isMounted) this.onCompleted(data, remember);
                })
                .catch(err => {
                    this.onError('Failed to verify login attempt', err);
                });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const { offices } = this.state;
        if (offices === null) {
            this.loadOffices();
        }
    }

    redirection = () => {
        const { location, history } = this.props;
        const stashedReferrer = sessionStorage.getItem(SIGN_IN_REFERRER);
        sessionStorage.removeItem(SIGN_IN_REFERRER);
        const redirectPath = (location.state && location.state.referrer) || stashedReferrer || '/';
        history.replace(redirectPath);
    };

    loadOffices() {
        const that = this;
        getUtilitiesClient()
            .query({ query: getOfficeNames })
            .then(
                ({ data }) => {
                    if (!data || !data.readOfficesUsernames) {
                        that.onError('failed to get offices. data empty', data);
                        return;
                    }

                    const offices = data.readOfficesUsernames.edges.map(({ node }) => {
                        return { Title: node.BusinessName, ID: node.ID };
                    });

                    that.setState({ offices });
                },
                error => that.onError('failed to get offices', error)
            );
        //do this so that it doesn't try to infinitely load the offices
        this.setState({ offices: [] });
    }

    onError(msg, obj) {
        const errorMessage = msg + (obj && obj.message ? '. Reason: ' + obj.message : '');
        console.error(errorMessage);
    }

    render() {
        const { remember } = this.state;
        return (
            <AnonPage>
                {(isLocalClient() && !isLocalServer() && (
                    <Mutation
                        client={getAuthClient()}
                        mutation={loginMutation}
                        onCompleted={data => this._isMounted && this.onCompleted({ data }, remember)}
                    >
                        {this.renderForm}
                    </Mutation>
                )) || (
                    <>
                        <CircularProgress />
                        <p>Verifying...</p>
                    </>
                )}
            </AnonPage>
        );
    }

    renderForm = (mutate, results = {}) => {
        const { classes } = this.props;
        const { loading } = results;
        const { offices, email, password, lastofficeid, remember, error, busy } = this.state;
        const onSubmit = e => {
            if (e) {
                e.preventDefault();
            }

            if (loading) {
                return;
            }

            if (!email) {
                this.setState({ error: 'Please enter an email address.' });
                return;
            }

            if (!password) {
                this.setState({ error: 'Please enter a password.' });
                return;
            }

            if (!lastofficeid) {
                this.setState({ error: 'Please select an office.' });
                return;
            }
            this.setState({ busy: true }, () => {
                window.localStorage.removeItem('notificationSeenDate');
                mutate({
                    variables: { email, password }
                });
            });
        };

        // const resetPassword = "/forgot-password";
        const resetPassword = '//' + getServiceURLHostname() + '/Security/lostpassword';

        return (
            <form className={classes.form} onSubmit={onSubmit}>
                <Typography variant="title" gutterBottom>
                    Sign In
                </Typography>
                <div className={classes.dividerLine} />
                <Typography component="p" variant="body1">
                    If you have a Funeral Manager account please sign in below.
                </Typography>
                {error && (
                    <Paper className={classes.error}>
                        <Typography component="p" variant="body1" className={classes.errorText}>
                            {error}
                        </Typography>
                    </Paper>
                )}
                <div style={{ marginTop: 16, marginBottom: 0 }}>
                    <TextField
                        disabled={busy}
                        onChange={this.onChangeText('email')}
                        placeholder="Email"
                        value={email}
                        inputMode="email"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon color="primary">person</Icon>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>

                <div style={{ marginTop: 16, marginBottom: 0 }}>
                    <TextField
                        disabled={busy}
                        onChange={this.onChangeText('password')}
                        placeholder="Password"
                        value={password}
                        type="password"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon color="primary">lock</Icon>
                                </InputAdornment>
                            )
                        }}
                    />
                </div>

                <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <TextField
                        disabled={busy}
                        onChange={this.onChangeText('lastofficeid')}
                        label="Office"
                        value={lastofficeid}
                        select
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Icon color="primary">business</Icon>
                                </InputAdornment>
                            )
                        }}
                    >
                        {offices &&
                            offices.map(option => (
                                <MenuItem key={option.ID} value={option.ID}>
                                    {option.Title}
                                </MenuItem>
                            ))}
                    </TextField>
                </div>

                <div className={classes.rememberLinks}>
                    {(1 !== 2 && (
                        <FormControlLabel
                            control={<Checkbox checked={remember} onChange={this.onToggleRemember} color="primary" />}
                            label="Stay signed in"
                        />
                    )) || <span />}
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={busy}
                        color="primary"
                        className={classes.button}
                    >
                        Sign In {busy ? <Spinner /> : <NextIcon />}
                    </Button>
                </div>
                <div>
                    <Link to={resetPassword} className={classes.link} target={'_blank'}>
                        Lost/reset password
                    </Link>
                </div>
            </form>
        );
    };
}

const updateMemberLastOfficeIDMutation = gql`
    mutation UpdateMemberLastOfficeID($memberid: ID!, $lastofficeid: ID!) {
        updateMemberLastOfficeID(ID: $memberid, LastOfficeID: $lastofficeid) {
            ID
        }
    }
`;

const loginMutation = gql`
    mutation Login($email: String!, $password: String!) {
        createToken(Email: $email, Password: $password) {
            token
            member {
                ID
                FirstName
                Surname
            }
        }
    }
`;

const createTokenFromHash = gql`
    mutation createTokenFromHash($id: ID!, $hash: String!) {
        createTokenFromHash(id: $id, hash: $hash) {
            token
            member {
                ID
                FirstName
                Surname
            }
        }
    }
`;

const styles = ({ palette, typography, spacing, breakpoints }) => ({
    form: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: 320 + spacing(4),
        padding: spacing(2),
        margin: 'auto',
        [breakpoints.down('sm')]: {
            marginBottom: spacing(4) // extra space for silly mobile footers
        }
    },
    dividerLine: {
        width: 50,
        height: 4,
        backgroundColor: palette.action.active,
        margin: '16px 0'
    },
    link: {
        fontSize: '0.875rem'
    },
    rememberLinks: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    button: {
        margin: '12px 0',
        borderRadius: 25,
        width: 120,
        alignSelf: 'center',
        textTransform: 'none'
    },
    error: {
        backgroundColor: palette.error.light,
        padding: spacing(1),
        marginTop: spacing(2),
        marginBottom: spacing(1)
    },
    errorText: {
        color: palette.error.contrastText,
        fontWeight: typography.fontWeightMedium
    }
});

export default withStyles(styles)(SignIn);

export { SignIn, loginMutation, getOfficeNames, createTokenFromHash };
